import { naturalTravelMs, posePathNaturalInWindow } from '../format'
import { EAGLE_BOARD_MAP_IDS, mapsFromCatalog } from '../maps'
import { healSyncedTimeline } from '../resolveSession'
import { addStoryMs, storyAt } from '../storyTime'
import { buildClampedTornadoPreset, windFromOffset } from '../tornadoPresets'
import type { ScenarioSession } from '../types'

/** Sentinel story origin (America/Chicago, CDT). Internal math stays in ms from this instant. */
const ORIGIN = '2026-09-22T10:50:00-05:00'

/**
 * Client dig-in scenario: backhoe strikes gas at Yucca & Elm.
 *
 * Board time is real scenario elapsed from 10:50 (0:00): one script second = 1000 ms of math.
 * Every value written onto the session is an ISO via storyAt(ORIGIN, elapsed).
 * Preview/Play speed is controlled only by the strip runtime slider.
 *
 * Object-first spine: Include-on objects sync timeline cards from label + description;
 * narration beats are glue only (multi-phase same object, chat injects, hose group).
 * Approaches use hold-at-start so dispatch→on-scene timing still lines up on the map.
 */
const SCRIPT_START_MIN = 10 * 60 + 50
const SCRIPT_END_MIN = 11 * 60 + 18
/** Story window 10:50–11:18 (hose / long redeploy may extend past this). */
const SCRIPT_SPAN_MS = (SCRIPT_END_MIN - SCRIPT_START_MIN) * 60_000

/** Wall-clock hm → ms since scenario 0:00 (10:50). */
function at(h: number, m: number): number {
  const t = h * 60 + m
  return Math.max(0, (t - SCRIPT_START_MIN) * 60_000)
}

/** Meters east/north of a point → lng/lat (local flat-earth, fine for a neighborhood). */
function offsetMeters(
  origin: { lng: number; lat: number },
  eastM: number,
  northM: number,
): { lng: number; lat: number } {
  const dLat = northM / 111_320
  const dLng = eastM / (111_320 * Math.cos((origin.lat * Math.PI) / 180))
  return { lng: origin.lng + dLng, lat: origin.lat + dLat }
}

/**
 * After the erratic wander, weaken the wind and drift east, then hide.
 * The ring stays the cameo radius; the leak drops out because the tornado leaves.
 * Direction on the drift points along that eastward leg.
 * `hideAtMs` is the Show-on-board Until time.
 */
function withEastDissipation<
  T extends {
    keyframes: { at: string; lng: number; lat: number }[]
    windKeyframes: {
      at: string
      fromDeg: number
      speedMph: number
      plumeDistanceM: number
      plumeSpreadDeg: number
    }[]
  },
>(built: T, exitMs: number): T & { hideAt: string } {
  const lastPose = built.keyframes[built.keyframes.length - 1]
  const lastWind = built.windKeyframes[built.windKeyframes.length - 1]
  if (!lastPose || !lastWind) {
    return { ...built, hideAt: lastPose?.at ?? ORIGIN }
  }
  const t0 = lastPose.at
  const legs = [
    { u: 0.35, east: 800, north: 70 },
    { u: 0.7, east: 1700, north: -40 },
    { u: 1, east: 2800, north: 50 },
  ]
  const poseExtra = legs.map((leg) => {
    const p = offsetMeters(lastPose, leg.east, leg.north)
    return { at: addStoryMs(t0, Math.round(exitMs * leg.u)), lng: p.lng, lat: p.lat }
  })
  const windExtra = [
    { u: 0.35, east: 800, north: 70, speedMph: 16, plumeDistanceM: 240, plumeSpreadDeg: 20 },
    { u: 0.7, east: 1700, north: -40, speedMph: 9, plumeDistanceM: 120, plumeSpreadDeg: 14 },
    { u: 1, east: 2800, north: 50, speedMph: 5, plumeDistanceM: 70, plumeSpreadDeg: 10 },
  ].map((w) => ({
    at: addStoryMs(t0, Math.round(exitMs * w.u)),
    fromDeg: windFromOffset(w.east, w.north, lastWind.fromDeg),
    speedMph: w.speedMph,
    plumeDistanceM: w.plumeDistanceM,
    plumeSpreadDeg: w.plumeSpreadDeg,
  }))
  return {
    ...built,
    keyframes: [...built.keyframes, ...poseExtra],
    windKeyframes: [...built.windKeyframes, ...windExtra],
    hideAt: addStoryMs(t0, exitMs),
  }
}

/**
 * Cameo ring is a fixed radius, not the preset's neighborhood-scale footprint.
 * 340 m puts the dig-in leak (~65–660 m from the track) in and out as the tornado wanders.
 */
function shrinkTornadoInfluence<
  T extends {
    windKeyframes: {
      at: string
      fromDeg: number
      speedMph: number
      plumeDistanceM: number
      plumeSpreadDeg: number
    }[]
    params: {
      fromDeg: number
      speedMph: number
      plumeDistanceM: number
      plumeSpreadDeg: number
      showPlume: boolean
      radiusM?: number
    }
  },
>(built: T, peakInfluenceM: number): T {
  const max = Math.max(1, ...built.windKeyframes.map((f) => f.plumeDistanceM))
  const scale = peakInfluenceM / max
  const windKeyframes = built.windKeyframes.map((f) => ({
    ...f,
    plumeDistanceM: Math.max(60, Math.round(f.plumeDistanceM * scale)),
  }))
  const peak = windKeyframes.reduce((a, b) =>
    b.plumeDistanceM > a.plumeDistanceM ? b : a,
  )
  return {
    ...built,
    windKeyframes,
    params: {
      ...built.params,
      fromDeg: peak.fromDeg,
      speedMph: peak.speedMph,
      plumeDistanceM: peak.plumeDistanceM,
      plumeSpreadDeg: peak.plumeSpreadDeg,
      radiusM: peakInfluenceM,
    },
  }
}

/** Total natural travel ms along a polyline (~40 mph). */
function pathNaturalMs(points: readonly { lng: number; lat: number }[]): number {
  let total = 0
  for (let i = 1; i < points.length; i++) {
    total += naturalTravelMs(points[i - 1]!, points[i]!)
  }
  return Math.max(1, total)
}

const CAM = { lng: -115.478, lat: 33.8535, zoom: 15.2 }

/** Court St / Kaiser admin — L/J brown building used as FD staging. */
const FD = { lng: -115.48705, lat: 33.85655 }

/**
 * Auto-pan targets — times filled in create() with real scenario ms.
 * Index 1 (10:54 Engine 1 dispatch): framed like a trainer Save-camera on the
 * Engine 1 sync card — Court/Kaiser staging in-frame with the dig corridor
 * still readable (old dig-centered 15.2 clipped the departing engine).
 */
const CAMERA_BEAT_POSES = [
  { lng: -115.474251, lat: 33.852313, zoom: 17.47 },
  // Midway FD ↔ dig, zoomed out so Engine 1 at Court/Kaiser stays fully visible.
  { lng: -115.4812, lat: 33.8546, zoom: 14.7 },
  { lng: -115.473742, lat: 33.852173, zoom: 15.6 },
  { lng: -115.475756, lat: 33.85332, zoom: 15.01 },
  { lng: -115.472916, lat: 33.851535, zoom: 15.5 },
  { lng: -115.473702, lat: 33.851664, zoom: 15.56 },
  { lng: -115.472058, lat: 33.85106, zoom: 15.56 },
  { lng: -115.473736, lat: 33.852267, zoom: 15.28 },
  { lng: -115.474459, lat: 33.851512, zoom: 16.06 },
] as const

/** Dig-in on Yucca west of Elm (marker sits on the live pipe asset). */
const DIG_LEAK = { lng: -115.47455, lat: 33.85208 }
const DIG_BACKHOE = { lng: -115.4742, lat: 33.85235 }
const DIG_FIRE = { lng: -115.47455, lat: 33.85208 }
const DIG_CONTRACTOR = { lng: -115.47395, lat: 33.8525 }
const DIG_AIR = { lng: -115.47385, lat: 33.85165 }

/** First-due: Yucca just east of Eagle Mountain Dr. */
const ARRIVE = { lng: -115.47655, lat: 33.85208 }
/** Stage on Yucca west of Engine 1 — do not overshoot east toward the fire. */
const ARRIVE_E2 = { lng: -115.4769, lat: 33.85208 }

/** Engine 3 stages east of the dig-in on Yucca (from Kaiser), before school reposition. */
const ARRIVE_E3 = { lng: -115.47315, lat: 33.85208 }

const CLOSE_ELM_DESERT = { lng: -115.47437, lat: 33.85348 }
const CLOSE_YUCCA_KAISER = { lng: -115.46993, lat: 33.85208 }
const CLOSE_EM_BASELINE = { lng: -115.47705, lat: 33.85009 }

/** Engine 3 start — Kaiser Rd east of Yucca∩Kaiser (near R2 / east approach). */
const E3_START = { lng: -115.4641, lat: 33.85173 }

const SCHOOL = { lng: -115.4715, lat: 33.8509 }
/** Yucca Drive centerline lat for axis-aligned vehicle paths. */
const YUCCA_LAT = 33.85208
/** Shared Court/Kaiser → Yucca corridor (E1 ends at ARRIVE; E2 continues to ARRIVE_E2). */
const YUCCA_APPROACH_VIA = [
  { lat: 33.857649, lng: -115.486544 },
  { lat: 33.852071, lng: -115.479628 },
  { lng: -115.479, lat: YUCCA_LAT },
] as const
/** Eagle Mountain Rd centerline (N–S) for school south-lot approach. */
const EM_LNG = -115.47705
const EM_YUCCA = { lng: EM_LNG, lat: YUCCA_LAT }
/** South on Eagle Mountain Rd to the unmarked connector north of the school lot. */
const EM_SOUTH_TURN = { lng: -115.4770501, lat: 33.8502705 }
/** School parking just west of the "Eagle Mountain School" label (east of EM Rd). */
const SCHOOL_SOUTH = { lng: -115.4744364, lat: 33.850277 }
const SCHOOL_SOUTH_E3 = { lng: -115.4734784, lat: 33.8502216 }

/** Yucca ∩ Kaiser — east end of Yucca before the long-way school reposition. */
const YUCCA_KAISER = { lng: -115.46993, lat: YUCCA_LAT }
/** Kaiser further east of the Yucca merge — approach only (not school reposition). */
const KAISER_EAST = { lng: -115.4662737, lat: 33.8520678 }
/** First bend NW onto Kaiser from Yucca∩Kaiser (OSM Kaiser Rd). */
const KAISER_FROM_YUCCA = { lng: -115.47288, lat: 33.85338 }
/** NW tip of Median Kaiser Rd (Kaiser meets Median Kaiser) — do not cut across to Elm. */
const MEDIAN_KAISER_NW = { lng: -115.47912, lat: 33.85603 }
/** SE end of Median Kaiser where the name becomes Eagle Mountain Rd (at Elm). */
const MEDIAN_KAISER_EM = { lng: -115.4780402, lat: 33.8547601 }
/** Eagle Mountain Rd at Elm St, then south toward Yucca / school. */
const EM_ELM = { lng: -115.477307, lat: 33.85399 }
/** Eagle Mountain Rd mid-block between Elm and Yucca. */
const EM_MID = { lng: -115.477091, lat: 33.853503 }

/** Pipeline tech — Sage Dr home → Baseline → Smoketree → Yucca @ EM (hand-snapped). */
const SAGE_HOME = { lat: 33.8508641, lng: -115.4801894 }
const PERSONNEL_ROUTE = [
  SAGE_HOME,
  { lat: 33.8508602, lng: -115.4804016 },
  { lat: 33.8500885, lng: -115.4807082 },
  { lat: 33.8500769, lng: -115.4785822 },
  { lat: 33.8520476, lng: -115.4779323 },
  { lat: 33.8520496, lng: -115.4773307 },
] as const

/** Snapped centerline stubs for closed intersections (Road overlays, named to Mapbox streets). */
const ELM_STUB: [number, number][] = [
  [-115.47437, 33.8522],
  [-115.47437, 33.8528],
  [-115.47437, 33.85348],
  [-115.47437, 33.85405],
]
const YUCCA_KAISER_STUB: [number, number][] = [
  [-115.4715, 33.85208],
  [-115.4705, 33.85208],
  [-115.46993, 33.85208],
  [-115.4692, 33.85208],
]
const EM_BASELINE_STUB: [number, number][] = [
  [-115.47705, 33.8512],
  [-115.47705, 33.8505],
  [-115.47705, 33.85009],
  [-115.47705, 33.8495],
]

/** Hose lines from each engine's final pose toward the dig-in / fire on Yucca. */
const HOSE_E1: [number, number][] = [
  [ARRIVE.lng, ARRIVE.lat],
  [-115.4758, 33.8521],
  [-115.4752, 33.85208],
  [-115.4747, 33.85208],
]
const HOSE_E2: [number, number][] = [
  [SCHOOL_SOUTH.lng, SCHOOL_SOUTH.lat],
  [-115.47445, 33.8509],
  [-115.4745, 33.8515],
  [-115.47455, 33.85205],
]
const HOSE_E3: [number, number][] = [
  [SCHOOL_SOUTH_E3.lng, SCHOOL_SOUTH_E3.lat],
  [-115.4737, 33.8509],
  [-115.4741, 33.85155],
  [-115.4745, 33.85205],
]

export const YUCCA_DIG_IN_ID = 'yucca-elm-dig-in'

export function createYuccaDigInSession(): ScenarioSession {
  const t1050 = at(10, 50)
  const t1052 = at(10, 52)
  const t1054 = at(10, 54)
  const t1059 = at(10, 59)
  const t1101 = at(11, 1)
  const t1103 = at(11, 3)
  const t1104 = at(11, 4)
  const t1105 = at(11, 5)
  /** Original script: pipeline personnel on scene ahead of ignition. */
  const t1107 = at(11, 7)
  const t1108 = at(11, 8)
  /** ~5s after backhoe starts moving; still before Engine 2/3 at 11:09. */
  const tIgn = t1108 + 5000
  const t1109 = at(11, 9)
  const t1111 = at(11, 11)
  const t1113 = at(11, 13)
  const t1114 = at(11, 14)
  const t1115 = at(11, 15)
  const t1116 = at(11, 16)
  const t1117 = at(11, 17)

  /** E2 school hop — short. */
  const e2RedeployPts = [ARRIVE_E2, EM_YUCCA, EM_SOUTH_TURN, SCHOOL_SOUTH]
  /** E3 long-way school loop. */
  const e3RedeployPts = [
    ARRIVE_E3,
    YUCCA_KAISER,
    KAISER_FROM_YUCCA,
    MEDIAN_KAISER_NW,
    MEDIAN_KAISER_EM,
    EM_ELM,
    EM_MID,
    EM_YUCCA,
    EM_SOUTH_TURN,
    SCHOOL_SOUTH_E3,
  ]
  // E3 long loop runs at natural ~40 mph from 11:16 (not crammed into E2's 1-minute hop).
  const e3RedeployEnd = t1116 + pathNaturalMs(e3RedeployPts)
  // Hoses wait until Engine 3 finishes the long loop (may run a few minutes past 11:18).
  const hoseMs = e3RedeployEnd
  // Snap board end up to a whole minute so the planned window stays a clean clock span.
  const boardContentMs = Math.max(SCRIPT_SPAN_MS, hoseMs)
  const boardEndMs =
    Math.ceil(Math.max(1, boardContentMs) / 60_000) * 60_000

  /** Neighborhood tornado — inactive until Active is turned on. Wander, then fade east. */
  const tornadoOrigin = { lng: DIG_LEAK.lng - 0.00135, lat: DIG_LEAK.lat + 0.00095 }
  // Peak influence ~340 m: dig-in is ~65–660 m from the track, so the leak flips in/out.
  const tornadoCameo = shrinkTornadoInfluence(
    withEastDissipation(
      buildClampedTornadoPreset('erratic', {
        origin: tornadoOrigin,
        onsetMs: at(10, 58),
        spanMs: 7 * 60_000,
        clockEndMs: boardEndMs,
        originIso: ORIGIN,
      }),
      4 * 60_000,
    ),
    340,
  )

  const cameraBeats = [
    { at: storyAt(ORIGIN, t1052), ...CAMERA_BEAT_POSES[0]! },
    // Owned by the Engine 1 sync card — same as Save camera on that row.
    {
      at: storyAt(ORIGIN, t1054),
      ...CAMERA_BEAT_POSES[1]!,
      eventId: 'sync-engine-1',
    },
    { at: storyAt(ORIGIN, t1059), ...CAMERA_BEAT_POSES[2]! },
    { at: storyAt(ORIGIN, t1109), ...CAMERA_BEAT_POSES[3]! },
    { at: storyAt(ORIGIN, t1111), ...CAMERA_BEAT_POSES[4]! },
    { at: storyAt(ORIGIN, t1114), ...CAMERA_BEAT_POSES[5]! },
    { at: storyAt(ORIGIN, t1115), ...CAMERA_BEAT_POSES[6]! },
    { at: storyAt(ORIGIN, t1116), ...CAMERA_BEAT_POSES[7]! },
    { at: storyAt(ORIGIN, hoseMs), ...CAMERA_BEAT_POSES[8]! },
  ]

  const session: ScenarioSession = {
    id: YUCCA_DIG_IN_ID,
    title: 'Backhoe hits gas line at Yucca & Elm',
    intro: 'Quiet morning in Eagle Mountain — then a dig-in at Yucca & Elm.',
    summary:
      'A backhoe strikes a natural-gas distribution line at Yucca Drive & Elm Street. Fire arrives, roads close, the operator is ID’d, then ignition forces mutual aid and a school evacuation.',
    storyStartTime: ORIGIN,
    storyEndTime: storyAt(ORIGIN, boardEndMs),
    timezone: 'America/Chicago',
    showTimeZone: false,
    displayUnits: 'imperial',
    sessionType: 'exercise',
    boardComplexity: 'everything',
    maps: mapsFromCatalog(EAGLE_BOARD_MAP_IDS),
    activeMapId: 'eagle-mountain',
    camera: CAM,
    cameraBeats: cameraBeats.map((b) => ({ ...b })),
    comments: [
      {
        id: 'yc-c-911',
        createdAt: storyAt(ORIGIN, t1052),
        createdAtWallIso: '2026-09-14T17:52:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: '911: Excavator reports backhoe hit underground gas line — Yucca Drive & Elm Street.',
      },
      {
        id: 'yc-c-dispatch',
        createdAt: storyAt(ORIGIN, t1054),
        createdAtWallIso: '2026-09-14T17:54:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'Engine 1 responding from Court / Kaiser staging.',
      },
      {
        id: 'yc-c-op',
        createdAt: storyAt(ORIGIN, t1103),
        createdAtWallIso: '2026-09-14T18:03:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'Operator ID’d: City of Eagle Mountain Natural Gas (Distribution). Emergency #: (760) 555-0199 (demo).',
      },
      {
        id: 'yc-c-ign',
        createdAt: storyAt(ORIGIN, tIgn),
        createdAtWallIso: '2026-09-14T18:08:07.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'Ignition reported at the dig-in.',
      },
      {
        id: 'yc-c-mutual',
        createdAt: storyAt(ORIGIN, t1109),
        createdAtWallIso: '2026-09-14T18:09:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'Mutual aid: Engine 2 from staging; Engine 3 from the east on Kaiser Rd.',
      },
      {
        id: 'yc-c-evac',
        createdAt: storyAt(ORIGIN, t1115),
        createdAtWallIso: '2026-09-14T18:15:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'Request evacuation of the school — fire spreading SE along Yucca.',
      },
    ],
    objects: [
      {
        id: 'wind-1',
        type: 'wind',
        label: 'Surface wind',
        scope: 'global',
        pose: null,
        size: 'md',
        description: 'Board wind that drives plume direction and distance for gas hazards.',
        showOnTimeline: false,
        params: {
          fromDeg: 280,
          speedMph: 10,
          plumeDistanceM: 420,
          plumeSpreadDeg: 26,
        },
        comments: [],
      },
      {
        id: 'icp-1',
        type: 'icp',
        label: 'West command',
        mapLabel: 'Dispatch',
        scope: 'local',
        pose: { lng: FD.lng - 0.00035, lat: FD.lat + 0.0002 },
        size: 'md',
        description: 'Incident command / dispatch post at Court & Kaiser staging.',
        showOnTimeline: false,
        params: {},
        comments: [],
      },
      {
        id: 'tornado-cameo-1',
        type: 'tornado',
        label: 'Freak Tornado',
        mapLabel: 'Freak tornado',
        scope: 'local',
        pose: tornadoOrigin,
        size: 'lg',
        ignored: true,
        description:
          'Touches down northwest of Yucca & Elm, whips through the neighborhood, then weakens and drifts east until it is gone.',
        startsAt: storyAt(ORIGIN, tornadoCameo.onsetMs),
        showOnTimeline: false,
        endsAt: tornadoCameo.hideAt,
        params: tornadoCameo.params,
        keyframes: tornadoCameo.keyframes,
        windKeyframes: tornadoCameo.windKeyframes,
        comments: [],
      },
      {
        id: 'school-1',
        type: 'school',
        label: 'School',
        mapLabel: 'School',
        scope: 'local',
        pose: SCHOOL,
        size: 'lg',
        description: 'Eagle Mountain School — later evacuation target as fire spreads SE on Yucca.',
        showOnTimeline: false,
        params: {},
        comments: [],
      },
      {
        id: 'backhoe-1',
        type: 'backhoe',
        label: 'Backhoe',
        mapLabel: 'Backhoe',
        scope: 'local',
        pose: DIG_BACKHOE,
        size: 'lg',
        description: 'On site at the Yucca & Elm dig before the 911 call. Moves briefly when the contractor clears the line.',
        params: {},
        keyframes: [
          { at: storyAt(ORIGIN, t1050), lng: DIG_BACKHOE.lng, lat: DIG_BACKHOE.lat },
          { at: storyAt(ORIGIN, t1108), lng: DIG_BACKHOE.lng, lat: DIG_BACKHOE.lat },
          { at: storyAt(ORIGIN, t1108 + 2500), lng: DIG_BACKHOE.lng + 0.00035, lat: DIG_BACKHOE.lat + 0.00012 },
          { at: storyAt(ORIGIN, t1108 + 5000), lng: DIG_BACKHOE.lng + 0.0002, lat: DIG_BACKHOE.lat + 0.00005 },
        ],
        comments: [],
      },
      {
        id: 'contractor-1',
        type: 'contractor',
        label: 'Contractor',
        mapLabel: 'Contractor',
        scope: 'local',
        pose: DIG_CONTRACTOR,
        size: 'md',
        description: 'On site with the backhoe before the release; attempts to move equipment at 11:08.',
        showOnTimeline: false,
        params: {},
        comments: [],
      },
      {
        id: 'pipe-1',
        type: 'gas_leak',
        label: 'Gas leak — Yucca & Elm',
        scope: 'local',
        pose: DIG_LEAK,
        size: 'md',
        description: 'Underground gas line struck. Release on the distribution line at Yucca & Elm.',
        startsAt: storyAt(ORIGIN, t1052),
        visibilityKeyframes: [{ at: storyAt(ORIGIN, tIgn), visible: false }],
        params: {
          radiusM: 40,
          showPlume: true,
        },
        comments: [],
      },
      {
        id: 'engine-1',
        type: 'engine',
        label: 'Engine 1',
        mapLabel: 'Engine 1',
        scope: 'local',
        pose: FD,
        size: 'lg',
        description: 'Leaves Court / Kaiser and runs Yucca toward the dig-in.',
        startsAt: storyAt(ORIGIN, t1054),
        params: {},
        keyframes: posePathNaturalInWindow(
          storyAt(ORIGIN, t1054),
          storyAt(ORIGIN, t1059),
          [FD, ...YUCCA_APPROACH_VIA, ARRIVE],
          'origin',
          20_000,
        ),
        comments: [],
      },
      {
        id: 'road-elm-desert',
        type: 'road',
        label: 'Elm @ Desert',
        scope: 'local',
        pose: CLOSE_ELM_DESERT,
        size: 'md',
        description: 'LE closes Elm at Desert — first of three roadblocks around the dig-in.',
        startsAt: storyAt(ORIGIN, t1101),
        path: ELM_STUB,
        params: {
          status: 'closed',
          segments: [ELM_STUB],
          attachedAssets: [
            {
              kind: 'road',
              name: 'Elm Street',
              properties: { name: 'Elm Street', class: 'street' },
              path: ELM_STUB,
              segments: [ELM_STUB],
            },
          ],
        },
        comments: [],
      },
      {
        id: 'road-yucca-kaiser',
        type: 'road',
        label: 'Yucca @ Kaiser',
        scope: 'local',
        pose: CLOSE_YUCCA_KAISER,
        size: 'md',
        description: 'LE closes Yucca at Kaiser on the east approach.',
        startsAt: storyAt(ORIGIN, t1101),
        path: YUCCA_KAISER_STUB,
        params: {
          status: 'closed',
          segments: [YUCCA_KAISER_STUB],
          attachedAssets: [
            {
              kind: 'road',
              name: 'Yucca Drive',
              properties: { name: 'Yucca Drive', class: 'secondary' },
              path: YUCCA_KAISER_STUB,
              segments: [YUCCA_KAISER_STUB],
            },
          ],
        },
        comments: [],
      },
      {
        id: 'road-em-baseline',
        type: 'road',
        label: 'Eagle Mountain @ Baseline',
        scope: 'local',
        pose: CLOSE_EM_BASELINE,
        size: 'md',
        description: 'LE closes Eagle Mountain Road at Baseline south of the scene.',
        startsAt: storyAt(ORIGIN, t1101),
        path: EM_BASELINE_STUB,
        params: {
          status: 'closed',
          segments: [EM_BASELINE_STUB],
          attachedAssets: [
            {
              kind: 'road',
              name: 'Eagle Mountain Road',
              properties: { name: 'Eagle Mountain Road', class: 'secondary' },
              path: EM_BASELINE_STUB,
              segments: [EM_BASELINE_STUB],
            },
          ],
        },
        comments: [],
      },
      {
        id: 'le-1',
        type: 'police',
        label: 'LE — Elm @ Desert',
        mapLabel: 'LE',
        scope: 'local',
        pose: CLOSE_ELM_DESERT,
        size: 'md',
        description: 'Law enforcement holding the Elm @ Desert closure.',
        startsAt: storyAt(ORIGIN, t1101),
        showOnTimeline: false,
        params: {},
        comments: [],
      },
      {
        id: 'le-2',
        type: 'police',
        label: 'LE — Yucca @ Kaiser',
        mapLabel: 'LE',
        scope: 'local',
        pose: CLOSE_YUCCA_KAISER,
        size: 'md',
        description: 'Law enforcement holding the Yucca @ Kaiser closure.',
        startsAt: storyAt(ORIGIN, t1101),
        showOnTimeline: false,
        params: {},
        comments: [],
      },
      {
        id: 'le-3',
        type: 'police',
        label: 'LE — EM @ Baseline',
        mapLabel: 'LE',
        scope: 'local',
        pose: CLOSE_EM_BASELINE,
        size: 'md',
        description: 'Law enforcement holding the Eagle Mountain @ Baseline closure.',
        startsAt: storyAt(ORIGIN, t1101),
        showOnTimeline: false,
        params: {},
        comments: [],
      },
      {
        id: 'banner-roads',
        type: 'label',
        label: '⚠  ROADS CLOSED  ⚠\nElm @ Desert\nYucca @ Kaiser\nEM @ Baseline',
        scope: 'local',
        pose: { lng: -115.4715, lat: 33.85375 },
        size: 'lg',
        description: 'Commander banner listing the three closed intersections.',
        startsAt: storyAt(ORIGIN, t1101),
        showOnTimeline: false,
        params: {
          text: '⚠  ROADS CLOSED  ⚠\nElm @ Desert\nYucca @ Kaiser\nEM @ Baseline',
          color: '#E24B4A',
        },
        comments: [],
      },
      {
        id: 'air-1',
        type: 'air_monitoring',
        label: 'Air monitoring',
        mapLabel: 'Air',
        scope: 'local',
        pose: DIG_AIR,
        size: 'md',
        description: 'Monitoring up at the dig-in after the operator is ID’d.',
        startsAt: storyAt(ORIGIN, t1104),
        params: {},
        comments: [],
      },
      {
        id: 'personnel-1',
        type: 'pipeline_personnel',
        label: 'Pipeline personnel',
        mapLabel: 'Pipeline rep',
        scope: 'local',
        pose: SAGE_HOME,
        size: 'md',
        description: 'Responding from Sage Dr toward Yucca & Elm.',
        startsAt: storyAt(ORIGIN, t1105),
        params: {},
        // Leaves with the 11:05 card; on scene with the 11:07 card.
        keyframes: posePathNaturalInWindow(storyAt(ORIGIN, t1105), storyAt(ORIGIN, t1107), [...PERSONNEL_ROUTE], 'origin'),
        comments: [],
      },
      {
        id: 'fire-1',
        type: 'fire',
        label: 'Ignition at dig-in',
        mapLabel: 'Fire',
        scope: 'local',
        pose: DIG_FIRE,
        size: 'lg',
        description:
          'Fire at the dig-in after the backhoe move. Gas-leak marker hides so the ignition reads clearly.',
        startsAt: storyAt(ORIGIN, tIgn),
        params: { radiusM: 45 },
        radiusKeyframes: [
          { at: storyAt(ORIGIN, tIgn), value: 45 },
          { at: storyAt(ORIGIN, t1114), value: 110 },
          { at: storyAt(ORIGIN, t1116), value: 160 },
        ],
        comments: [],
      },
      {
        id: 'engine-2',
        type: 'engine',
        label: 'Engine 2',
        mapLabel: 'Engine 2',
        scope: 'local',
        pose: FD,
        size: 'lg',
        description: 'Mutual aid from Court / Kaiser down Yucca toward Engine 1.',
        startsAt: storyAt(ORIGIN, t1109),
        params: {},
        keyframes: (() => {
          // Hold at FD so arrival ≈ 11:11 on-scene; stay on Yucca until 11:16 redeploy.
          const approach = posePathNaturalInWindow(storyAt(ORIGIN, t1109), storyAt(ORIGIN, t1111),
            [FD, ...YUCCA_APPROACH_VIA, ARRIVE_E2],
            'origin',
          )
          const redeploy = posePathNaturalInWindow(storyAt(ORIGIN, t1116), storyAt(ORIGIN, t1117), e2RedeployPts, 'origin')
          return [...approach, ...redeploy]
        })(),
        comments: [],
      },
      {
        id: 'engine-3',
        type: 'engine',
        label: 'Engine 3',
        mapLabel: 'Engine 3',
        scope: 'local',
        pose: E3_START,
        size: 'lg',
        description: 'Approaches from the east on Kaiser Rd toward the fire.',
        startsAt: storyAt(ORIGIN, t1109),
        params: {},
        keyframes: (() => {
          const approach = posePathNaturalInWindow(storyAt(ORIGIN, t1109), storyAt(ORIGIN, t1113),
            [E3_START, KAISER_EAST, YUCCA_KAISER, ARRIVE_E3],
            'origin',
          )
          // Long loop at natural speed from 11:16 (window ≈ travel → little/no end hold).
          const redeploy = posePathNaturalInWindow(storyAt(ORIGIN, t1116), storyAt(ORIGIN, e3RedeployEnd), e3RedeployPts, 'origin')
          return [...approach, ...redeploy]
        })(),
        comments: [],
      },
      {
        id: 'evac-1',
        type: 'evacuate',
        label: 'Evacuation — school',
        mapLabel: 'Evacuation',
        scope: 'local',
        pose: SCHOOL,
        size: 'lg',
        description: 'Evacuation zone around the school campus as fire pushes southeast on Yucca.',
        startsAt: storyAt(ORIGIN, t1115),
        params: { radiusM: 220 },
        comments: [],
      },
      {
        id: 'hose-1',
        type: 'sketch',
        label: 'Hose — Engine 1',
        scope: 'local',
        pose: { lng: HOSE_E1[0]![0], lat: HOSE_E1[0]![1] },
        size: 'md',
        path: HOSE_E1,
        description: 'Supply line from Engine 1’s Yucca stage toward the dig-in / fire.',
        startsAt: storyAt(ORIGIN, hoseMs),
        showOnTimeline: false,
        params: { color: '#185FA5', width: 5, opacity: 0.9 },
        comments: [],
      },
      {
        id: 'hose-label',
        type: 'label',
        label: 'Hose lines',
        scope: 'local',
        // Lower-left (SW) quadrant of the fire zone circle.
        pose: { lng: DIG_FIRE.lng - 0.00028, lat: DIG_FIRE.lat - 0.00022 },
        size: 'md',
        description: 'Group caption for the three engine hose lays at the dig-in.',
        startsAt: storyAt(ORIGIN, hoseMs),
        showOnTimeline: false,
        params: { text: 'Hose lines', color: '#ffffff' },
        comments: [],
      },
      {
        id: 'hose-2',
        type: 'sketch',
        label: 'Hose — Engine 2',
        scope: 'local',
        pose: { lng: HOSE_E2[0]![0], lat: HOSE_E2[0]![1] },
        size: 'md',
        path: HOSE_E2,
        description: 'Supply line from Engine 2’s school-lot position toward the fire.',
        startsAt: storyAt(ORIGIN, hoseMs),
        showOnTimeline: false,
        params: { color: '#185FA5', width: 5, opacity: 0.9 },
        comments: [],
      },
      {
        id: 'hose-3',
        type: 'sketch',
        label: 'Hose — Engine 3',
        scope: 'local',
        pose: { lng: HOSE_E3[0]![0], lat: HOSE_E3[0]![1] },
        size: 'md',
        path: HOSE_E3,
        description: 'Supply line from Engine 3’s lot position toward the fire.',
        startsAt: storyAt(ORIGIN, hoseMs),
        showOnTimeline: false,
        params: { color: '#185FA5', width: 5, opacity: 0.9 },
        comments: [],
      },
    ],
    // Glue only — Include-on objects sync their own cards (label + description).
    timeline: [
      {
        id: 'ev-arrive',
        at: storyAt(ORIGIN, t1059),
        title: 'Engine 1 on scene',
        summary: 'Parked on Yucca just east of Eagle Mountain Drive.',
        iconType: 'engine',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-op',
        at: storyAt(ORIGIN, t1103),
        title: 'Operator identified',
        summary: 'City of Eagle Mountain Natural Gas — details in chat.',
        iconType: 'label',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-em',
        at: storyAt(ORIGIN, t1103),
        title: 'Pipeline rep contacted',
        summary: 'Emergency number called after operator ID — see chat.',
        iconType: 'label',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-pers-arr',
        at: storyAt(ORIGIN, t1107),
        title: 'Pipeline personnel on scene',
        summary: 'On scene — stages west of Engine 1.',
        iconType: 'pipeline_personnel',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-move',
        at: storyAt(ORIGIN, t1108),
        title: 'Contractor moves backhoe',
        summary: 'Contractor tries to clear the backhoe off the line.',
        iconType: 'contractor',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-e2-arr',
        at: storyAt(ORIGIN, t1111),
        title: 'Engine 2 on scene',
        summary: 'Stages near Engine 1 on Yucca.',
        iconType: 'engine',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-e3-arr',
        at: storyAt(ORIGIN, t1113),
        title: 'Engine 3 on scene',
        summary: 'Stages east of the fire on Yucca after the longer Kaiser approach.',
        iconType: 'engine',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-spread',
        at: storyAt(ORIGIN, t1114),
        title: 'Fire spreading SE',
        summary: 'Burn grows along the southeast side of Yucca toward the school.',
        iconType: 'fire',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-redeploy',
        at: storyAt(ORIGIN, t1116),
        title: 'Engines to school south lot',
        summary: 'Engines reposition to the school south lot.',
        iconType: 'engine',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
      {
        id: 'ev-hose',
        at: storyAt(ORIGIN, hoseMs),
        title: 'Hose lines deployed',
        summary: 'Hose lines from Engines 1–3 into the fire.',
        iconType: 'sketch',
        hiddenFromParticipants: false,
        kind: 'narration',
        actions: [],
      },
    ],
  }
  return healSyncedTimeline(session)
}
